import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Megaphone,
  Code2,
  CheckCircle2,
  ArrowUpRight,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import TID from "@/lib/testIds";

const services = [
  {
    key: "pentesting",
    title: "Penetration Testing",
    tag: "Cybersecurity",
    icon: ShieldCheck,
    accent: "from-blue-500 to-indigo-600",
    description:
      "Comprehensive offensive security to uncover vulnerabilities before attackers do.",
    items: [
      "Web Application Testing",
      "API Testing",
      "Mobile Security Testing",
      "Network Penetration Testing",
      "Vulnerability Assessment",
      "Detailed Security Reports",
      "OWASP Top 10 Coverage",
      "Compliance Support",
    ],
    ctaLabel: "Request Free Security Audit",
    ctaTid: TID.servicesAuditCta,
  },
  {
    key: "marketing",
    title: "Social Media Marketing",
    tag: "Growth & Ads",
    icon: Megaphone,
    accent: "from-violet-500 to-fuchsia-600",
    description:
      "Performance-driven campaigns that turn attention into pipeline and revenue.",
    items: [
      "Facebook Ads",
      "Instagram Ads",
      "Google Ads",
      "SEO & Content Strategy",
      "Branding & Identity",
      "Content Creation",
      "Analytics & Reporting",
      "Lead Generation",
    ],
    ctaLabel: "Grow My Business",
    ctaTid: TID.servicesGrowCta,
  },
  {
    key: "webdev",
    title: "Website Development",
    tag: "Engineering",
    icon: Code2,
    accent: "from-sky-500 to-cyan-500",
    description:
      "High-performance websites, landing pages, and storefronts built to convert.",
    items: [
      "Business Websites",
      "Landing Pages",
      "Ecommerce",
      "Performance Optimization",
      "Maintenance & Support",
    ],
    ctaLabel: "Start a Project",
    ctaTid: "services-webdev-cta",
  },
];

const cardV = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Services = () => {
  return (
    <section
      id="services"
      data-testid={TID.servicesSection}
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-semibold text-brand-blue uppercase tracking-[0.18em]"
          >
            What we do
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
          >
            Security that protects.{" "}
            <span className="text-gradient-brand">Marketing that performs.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-slate-600 dark:text-slate-300 md:text-lg"
          >
            From in-depth penetration testing to ROI-focused ad campaigns and
            modern websites, we combine engineering rigor with creative growth.
          </motion.p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {services.map((s, i) => {
            const Icon = s.icon;
            const isWhatsApp = s.key === "webdev";
            return (
              <motion.article
                key={s.key}
                data-testid={TID.serviceCard(s.key)}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                variants={cardV}
                whileHover={{ y: -6 }}
                className="group relative rounded-3xl border border-slate-200/70 dark:border-white/10 bg-white dark:bg-[#0F172A]/70 p-7 md:p-8 shadow-elevated overflow-hidden transition-shadow hover:shadow-[0_30px_80px_-20px_rgba(37,99,235,0.25)]"
              >
                <div
                  aria-hidden
                  className={`absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br ${s.accent} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`}
                />

                <div className="relative flex items-start justify-between">
                  <span
                    className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${s.accent} text-white shadow-lg`}
                  >
                    <Icon className="w-7 h-7" />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-white/5">
                    {s.tag}
                  </span>
                </div>

                <h3 className="mt-6 font-display text-2xl font-bold text-slate-900 dark:text-white">
                  {s.title}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300 text-sm md:text-base leading-relaxed">
                  {s.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {s.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300"
                    >
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-brand-blue flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex flex-wrap gap-2">
                  <Button
                    data-testid={s.ctaTid}
                    onClick={() => scrollTo("contact")}
                    className="h-10 px-5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold hover:opacity-90"
                  >
                    {s.ctaLabel}
                    <ArrowUpRight className="w-4 h-4 ml-1.5" />
                  </Button>
                  {isWhatsApp && (
                    <a
                      href={buildWhatsAppLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="services-whatsapp-cta"
                      className="h-10 inline-flex items-center px-4 rounded-full border border-slate-200 dark:border-white/10 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
                    >
                      <MessageCircle className="w-4 h-4 mr-1.5 text-emerald-500" />
                      WhatsApp
                    </a>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
